import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Share, Plus, Smartphone, ChevronUp } from 'lucide-react';

// Helper functions to check device/install status
const checkIsStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone 
    || document.referrer.includes('android-app://');
};

const checkIsIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const checkIsMobile = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const checkIsAndroid = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showFullScreenPrompt, setShowFullScreenPrompt] = useState(false);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [showTopBanner, setShowTopBanner] = useState(false);
  
  // Compute device info once
  const isStandalone = checkIsStandalone();
  const isIOS = checkIsIOS();
  const isMobile = checkIsMobile();
  const isAndroid = checkIsAndroid();

  useEffect(() => {
    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('SW registered:', registration.scope);
        })
        .catch((err) => {
          console.log('SW registration failed:', err);
        });
    }

    // Check if already installed
    const alreadyInstalled = localStorage.getItem('appInstalled') === 'true';
    if (alreadyInstalled || isStandalone) {
      return;
    }

    // Check dismissal status
    const dismissedThisSession = sessionStorage.getItem('installPromptDismissedSession');
    const permanentlyDismissed = localStorage.getItem('installPromptPermanentlyDismissed');
    
    // Track visit count
    const visitCount = parseInt(localStorage.getItem('visitCount') || '0') + 1;
    localStorage.setItem('visitCount', visitCount.toString());

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('beforeinstallprompt fired');
      
      if (!permanentlyDismissed) {
        if (dismissedThisSession) {
          // Show floating button instead
          setShowFloatingButton(true);
        } else {
          // Show full screen prompt after brief delay
          setTimeout(() => {
            setShowFullScreenPrompt(true);
          }, 1000);
        }
      } else {
        // Just show floating button for permanently dismissed
        setShowFloatingButton(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // ENHANCED FALLBACK: For mobile devices, show banner immediately
    // Don't wait for beforeinstallprompt which may never fire
    if (isMobile && !isStandalone && !permanentlyDismissed) {
      // Show top banner immediately for mobile users
      setTimeout(() => {
        if (!dismissedThisSession) {
          setShowTopBanner(true);
          setShowFloatingButton(true);
        } else {
          setShowFloatingButton(true);
        }
      }, 500);

      // If no beforeinstallprompt after 3 seconds, show full prompt
      const fallbackTimeout = setTimeout(() => {
        if (!deferredPrompt && !dismissedThisSession && !permanentlyDismissed) {
          setShowFullScreenPrompt(true);
          setShowTopBanner(false);
        }
      }, 3000);

      return () => {
        clearTimeout(fallbackTimeout);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    // Listen for successful installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setShowFullScreenPrompt(false);
      setShowFloatingButton(false);
      setShowTopBanner(false);
      localStorage.setItem('appInstalled', 'true');
    };
    
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone, isIOS, isMobile, deferredPrompt]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setShowPrompt(false);
          setShowFullScreenPrompt(false);
          setShowFloatingButton(false);
          setShowTopBanner(false);
          localStorage.setItem('appInstalled', 'true');
        }
        setDeferredPrompt(null);
      } catch (err) {
        console.log('Install prompt error:', err);
      }
    } else {
      // No deferred prompt - show instructions
      setShowFullScreenPrompt(true);
      setShowTopBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowFullScreenPrompt(false);
    setShowTopBanner(false);
    sessionStorage.setItem('installPromptDismissedSession', 'true');
    // Keep floating button visible
    setShowFloatingButton(true);
  };

  const handlePermanentDismiss = () => {
    setShowPrompt(false);
    setShowFullScreenPrompt(false);
    setShowFloatingButton(false);
    setShowTopBanner(false);
    localStorage.setItem('installPromptPermanentlyDismissed', 'true');
    sessionStorage.setItem('installPromptDismissedSession', 'true');
  };

  const handleLater = () => {
    setShowFullScreenPrompt(false);
    setShowTopBanner(false);
    sessionStorage.setItem('installPromptDismissedSession', 'true');
    setShowFloatingButton(true);
  };

  // Don't show anything if already installed
  if (isStandalone) {
    return null;
  }

  return (
    <>
      {/* TOP BANNER - Shows immediately on mobile */}
      {showTopBanner && isMobile && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-3 shadow-lg animate-slide-down">
          <div className="flex items-center justify-between max-w-screen-xl mx-auto">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Install Monyply App</p>
                <p className="text-xs text-emerald-100">Add to home screen for quick access</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-black hover:bg-gray-800 text-white font-bold px-4 h-9"
              >
                <Download className="w-4 h-4 mr-1" />
                Install
              </Button>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FLOATING INSTALL BUTTON - Always visible on mobile when not showing full prompt */}
      {showFloatingButton && isMobile && !showFullScreenPrompt && (
        <button
          onClick={handleInstallClick}
          className="fixed bottom-20 right-4 z-[90] w-14 h-14 bg-black text-white rounded-full shadow-2xl flex items-center justify-center animate-bounce-slow hover:scale-110 transition-transform"
          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
        >
          <Download className="w-6 h-6" />
        </button>
      )}

      {/* FULL SCREEN INSTALL PROMPT */}
      {showFullScreenPrompt && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl animate-scale-in">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10"
            >
              <X className="w-6 h-6" />
            </button>

            {/* App Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-white rounded-3xl shadow-xl flex items-center justify-center">
                <span className="text-5xl">💰</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center mb-2">
              Install Monyply
            </h2>
            <p className="text-emerald-100 text-center mb-6">
              Get the full app experience on your device
            </p>

            {/* Features */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 text-white">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-lg">⚡</span>
                </div>
                <span className="text-sm">Quick access from home screen</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-lg">📱</span>
                </div>
                <span className="text-sm">Works offline</span>
              </div>
              <div className="flex items-center space-x-3 text-white">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-lg">🔔</span>
                </div>
                <span className="text-sm">Get instant notifications</span>
              </div>
            </div>

            {/* Install Instructions for iOS */}
            {isIOS ? (
              <div className="bg-white/10 rounded-xl p-4 mb-6">
                <p className="text-white text-sm font-medium mb-3">To install on iOS:</p>
                <div className="space-y-2 text-emerald-100 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Tap the Share button <Share className="inline w-4 h-4" /></span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Scroll down and tap &quot;Add to Home Screen&quot; <Plus className="inline w-4 h-4" /></span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Tap &quot;Add&quot; to confirm</span>
                  </div>
                </div>
              </div>
            ) : isAndroid && !deferredPrompt ? (
              <div className="bg-white/10 rounded-xl p-4 mb-6">
                <p className="text-white text-sm font-medium mb-3">To install on Android:</p>
                <div className="space-y-2 text-emerald-100 text-sm">
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>Tap the menu button (⋮) in Chrome</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>Tap &quot;Add to Home screen&quot; or &quot;Install app&quot;</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-white/20 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>Tap &quot;Add&quot; or &quot;Install&quot; to confirm</span>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleInstallClick}
                className="w-full bg-black text-white hover:bg-gray-800 h-14 text-lg font-bold shadow-xl"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
              >
                <Download className="w-6 h-6 mr-2" />
                {deferredPrompt ? 'Install Now' : 'Got It!'}
              </Button>
              <button
                onClick={handleLater}
                className="w-full text-white hover:text-emerald-100 text-sm underline font-medium py-2"
              >
                Maybe later
              </button>
              <button
                onClick={handlePermanentDismiss}
                className="w-full text-white/60 hover:text-white/80 text-xs py-1"
              >
                Don&apos;t show again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BOTTOM PROMPT BAR - Smaller reminder */}
      {showPrompt && !showFullScreenPrompt && (
        <div className="fixed bottom-0 left-0 right-0 z-[80] bg-white border-t-2 border-emerald-500 shadow-2xl p-4 animate-slide-up md:hidden">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-2xl">💰</span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 text-sm">Install Monyply</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {isIOS 
                  ? 'Tap Share then "Add to Home Screen"' 
                  : 'Add to home screen for quick access'}
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  onClick={handleInstallClick}
                  size="sm"
                  className="bg-black hover:bg-gray-800 text-white h-9 px-4 font-bold"
                >
                  <Download className="w-4 h-4 mr-1" />
                  {deferredPrompt ? 'Install' : 'How to Install'}
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  size="sm"
                  className="h-9 px-3"
                >
                  Not Now
                </Button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s infinite;
        }
      `}</style>
    </>
  );
};

export default InstallPrompt;
